import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import OfficerNavbar from "./OfficerNavbar";
import * as XLSX from 'xlsx';
import { baseurl } from '../../url';
import StudentEditForm from "./StudentEditForm";
import { generatePrintHTML } from "./PrintPreviewComponent";
import "./StudentList.css";
import StudentPreviewModal from "./StudentPreviewModal";

/* ── Skeleton rows shown while fetching ─────────────── */
const SkeletonRows = ({ count = 8 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td><div className="skeleton skeleton-text" style={{ width: 24, margin: '0 auto' }} /></td>
        <td><div className="skeleton skeleton-text" style={{ width: '75%', margin: '0 auto' }} /></td>
        <td><div className="skeleton skeleton-text" style={{ width: 100, margin: '0 auto' }} /></td>
        <td><div className="skeleton skeleton-badge" /></td>
        <td><div className="skeleton skeleton-circle" /></td>
        <td><div className="skeleton skeleton-text" style={{ width: 90, margin: '0 auto' }} /></td>
        <td>
          <div className="action-btns">
            <div className="skeleton skeleton-btn" style={{ width: 62 }} />
            <div className="skeleton skeleton-btn" style={{ width: 46 }} />
            <div className="skeleton skeleton-btn" style={{ width: 50 }} />
            <div className="skeleton skeleton-btn" style={{ width: 56 }} />
          </div>
        </td>
      </tr>
    ))}
  </>
);

const StudentListOfficer = () => {
  const [approvedStudents, setApprovedStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);
  const [showAlumni, setShowAlumni] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectCourse, setSelectCourse] = useState("");
  const [alumniYears, setAlumniYears] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedAlumniYear, setSelectedAlumniYear] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewStudent, setPreviewStudent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
const [migrating, setMigrating] = useState(false);
const [showMigrationPreview, setShowMigrationPreview] = useState(false);
const [migrationPreview, setMigrationPreview] = useState(null);
  const fetchApprovedStudents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}/api/officerstudent/approvedStudents`);
      console.log("Fetched approved students:", response.data);
      setApprovedStudents(response.data.reverse());
    } catch (error) {
      console.error('Error fetching approved students:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAlumni = useCallback(async () => {
    try {
      const response = await axios.get(`${baseurl}/api/officerstudent/alumni`);
      setAlumni(response.data);
      const uniqueYears = [...new Set(response.data.map((s) => s.academicYear))];
      setAlumniYears(uniqueYears);
    } catch (error) {
      console.error("Error fetching alumni details:", error);
    }
  }, []);

  useEffect(() => {
    let filtered = [...approvedStudents];
    if (selectedCourse) filtered = filtered.filter((s) => s.course === selectedCourse);
    if (selectedSemester) filtered = filtered.filter((s) => s.semester === selectedSemester);
    if (searchTerm) {
      filtered = filtered.filter((s) =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.admissionNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.mobileNo?.includes(searchTerm) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredStudents(filtered);
  }, [approvedStudents, selectedCourse, selectedSemester, searchTerm]);

  useEffect(() => {
    let filtered = [...alumni];
    if (selectCourse) filtered = filtered.filter((s) => s.course === selectCourse);
    if (selectedAlumniYear) filtered = filtered.filter((s) => s.academicYear === selectedAlumniYear);
    setFilteredAlumni(filtered);
  }, [alumni, selectCourse, selectedAlumniYear]);

  useEffect(() => {
    fetchApprovedStudents();
    fetchAlumni();
  }, [fetchApprovedStudents, fetchAlumni]);

  const courseBadgeClass = (course) => {
    if (!course) return "course-badge";
    if (course.includes("CSE")) return "course-badge course-cse";
    if (course.includes("ECE")) return "course-badge course-ece";
    if (course.includes("BCA")) return "course-badge course-bca";
    if (course.includes("MCA")) return "course-badge course-mca";
    if (course.includes("BBA")) return "course-badge course-bba";
    return "course-badge";
  };

  const generateExcel = () => {
    const studentsToExport = filteredStudents.length > 0 ? filteredStudents : approvedStudents;
    const studentData = studentsToExport.map((student, idx) => ({
      'SI No': idx + 1,
      'Name': student.name,
      'Admission Number': student.admissionNumber,
      'Course': student.course,
      'Semester': student.semester,
      'Mobile No': student.mobileNo,
      'Email': student.email,
      'Admission Type': student.admissionType,
      'Allotment Category': student.allotmentCategory,
      'Fee Category': student.feeCategory,
      'Address': student.address,
      'Permanent Address': student.permanentAddress,
      'Pincode': student.pincode,
      'Religion': student.religion,
      'Community': student.community,
      'Category': student.category,
      'Date of Birth': student.dateOfBirth,
      'Gender': student.gender,
      'Blood Group': student.bloodGroup,
      'Whatsapp No': student.whatsappNo,
      'Entrance Exam Name': student.entranceExam,
      'Entrance Register No': student.entranceRollNo,
      'Entrance Rank': student.entranceRank,
      'Plus Two Board': student.marks?.boardType,
      'Plus Two Chemistry': student.marks?.chemistry,
      'Plus Two Physics': student.marks?.physics,
      'Plus Two Maths': student.marks?.maths,
      'Aadhaar Number': student.aadharNo,
      'Qualifying Exam Name': student.qualify?.exam,
      'Qualifying Exam Board': student.qualify?.board,
      'Institution Name': student.qualify?.institution,
      'Qualifying Exam Register Number': student.qualify?.regNo,
      'Exam Year and Month': student.qualify?.examMonthYear,
      'Percentage': student.qualify?.percentage,
      'CGPA': student.qualify?.cgpa,
      "Father's Name": student.parentDetails?.fatherName,
      "Father's Mobile No": student.parentDetails?.fatherMobileNo,
      "Father's Occupation": student.parentDetails?.fatherOccupation,
      "Mother's Name": student.parentDetails?.motherName,
      "Mother's Mobile No": student.parentDetails?.motherMobileNo,
      "Mother's Occupation": student.parentDetails?.motherOccupation,
      'Annual Income': student.annualIncome,
      'Nativity': student.nativity,
    }));

    const worksheet = XLSX.utils.json_to_sheet(studentData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

    let fileName = 'Students';
    if (selectedCourse) fileName += `_${selectedCourse}`;
    if (selectedSemester) fileName += `_Sem${selectedSemester}`;
    if (searchTerm) fileName += `_Filtered`;
    fileName += `_${new Date().toISOString().split('T')[0]}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const clearAllFilters = () => {
    setSelectedCourse("");
    setSelectedSemester("");
    setSearchTerm("");
    setSelectCourse("");
    setSelectedAlumniYear("");
  };

const handlePreviewMigration = async () => {
  setMigrating(true);
  try {
    const response = await axios.get(`${baseurl}/api/officer/preview-alumni-migration`);
    if (response.data.success) {
      setMigrationPreview(response.data);
      setShowMigrationPreview(true);
    } else {
      alert('Error: ' + response.data.message);
    }
  } catch (error) {
    console.error('Preview error:', error);
    alert('Error fetching preview: ' + (error.response?.data?.message || error.message));
  } finally {
    setMigrating(false);
  }
};

// Confirm migration after preview
const handleConfirmMigration = async () => {
  if (!window.confirm('Are you absolutely sure you want to move these students to alumni?\n\nThis action cannot be undone!')) {
    return;
  }
  
  setMigrating(true);
  try {
    const response = await axios.post(`${baseurl}/api/officer/migrate-alumni`, {
      confirmed: true,
      previewData: migrationPreview
    });
    
    if (response.data.success) {
      alert(`✅ Successfully migrated ${response.data.migratedCount} students to alumni!`);
      setShowMigrationPreview(false);
      setMigrationPreview(null);
      // Refresh the lists
      await fetchApprovedStudents();
      await fetchAlumni();
    } else {
      alert('Migration failed: ' + response.data.message);
    }
  } catch (error) {
    console.error('Migration error:', error);
    alert('Error during migration: ' + (error.response?.data?.message || error.message));
  } finally {
    setMigrating(false);
  }
};

  const handleDelete = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(`${baseurl}/api/officerstudent/deleteStudent/${studentId}`);
      setApprovedStudents((prev) => prev.filter((s) => s._id !== studentId));
      alert("Student deleted successfully.");
    } catch (error) {
      console.error("Error deleting student:", error);
      alert("Error deleting student.");
    }
  };

  const handlePreview = (student) => {
    setPreviewStudent(student);
    setShowPreview(true);
  };

  const handlePreviewEdit = () => {
    setShowPreview(false);
    handleEdit(previewStudent);
  };

  const handlePrintPreview = async (_id, photoUrl) => {
    try {
      const response = await axios.get(`${baseurl}/api/approvedstudentDetails/${_id}`);
      const studentDetails = response.data.studentDetails;
      if (!studentDetails) return;
      const htmlString = generatePrintHTML(studentDetails, null, photoUrl);
      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlString);
      printWindow.document.close();
    } catch (error) {
      console.error("Error generating print preview:", error);
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setStudentId(student._id);
    setEditMode(true);
  };

  const handleSaveComplete = async () => {
    setEditMode(false);
    setSelectedStudent(null);
    await fetchApprovedStudents();
  };

  const hasActiveFilters = selectedCourse || selectedSemester || searchTerm;

  return (
    <div>
      <OfficerNavbar />
      <div className="student-display-container">
        {!editMode ? (
          <div>
            {!showAlumni ? (
              <>
                {/* Page Header */}
                <div className="page-header">
                  <div>
                    <h1>Students</h1>
                    <p>View and manage all student records</p>
                  </div>
                  <span className="header-badge">{approvedStudents.length} total</span>
                </div>

                {/* Unified Toolbar */}
                <div className="toolbar">
                  <div className="toolbar-group">
                    <label className="filter-label">Course</label>
                    <select
                      className="filter-dropdown"
                      value={selectedCourse}
                      onChange={(e) => setSelectedCourse(e.target.value)}
                    >
                      <option value="">All courses</option>
                      <option value="B.Tech CSE">BTech CSE</option>
                      <option value="B.Tech ECE">BTech ECE</option>
                      <option value="BBA">BBA</option>
                      <option value="MCA">MCA</option>
                      <option value="BCA">BCA</option>
                    </select>
                  </div>

                  <div className="toolbar-divider" />

                  <div className="toolbar-group">
                    <label className="filter-label">Semester</label>
                    <select
                      className="filter-dropdown"
                      value={selectedSemester}
                      onChange={(e) => setSelectedSemester(e.target.value)}
                    >
                      <option value="">All semesters</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                        <option key={s} value={String(s)}>Semester {s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="toolbar-divider" />

                  <div className="toolbar-group">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Name, admission no, mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="toolbar-spacer" />

                  <button className="alumni-toggle-btn" onClick={() => setShowAlumni(true)}>
                    🎓 <span className="btn-label">Alumni</span>
                  </button>
                  <button className="export-btn" onClick={generateExcel}>
                    📊 <span className="btn-label">Export {filteredStudents.length > 0 && `(${filteredStudents.length})`}</span>
                  </button>
                  <button 
  className="migrate-btn" 
  onClick={handlePreviewMigration}
  disabled={migrating}
>
  {migrating ? '🔄 Loading...' : '🎓 Move Graduated to Alumni'}
</button>
                </div>

                {/* Active filter info strip */}
                {hasActiveFilters && (
                  <div className="filter-info">
                    <span>Showing {filteredStudents.length} of {approvedStudents.length} students</span>
                    <button onClick={clearAllFilters}>Clear filters</button>
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student</th>
                        <th>Admission No</th>
                        <th>Course</th>
                        <th>Sem</th>
                        <th>Mobile</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <SkeletonRows count={8} />
                      ) : filteredStudents.length > 0 ? (
                        filteredStudents.map((student, index) => (
                          <tr key={student._id}>
                            <td className="td-index">{index + 1}</td>
                            <td><strong>{student.name}</strong></td>
                            <td className="td-mono">{student.admissionNumber}</td>
                            <td>
                              <span className={courseBadgeClass(student.course)}>
                                {student.course}
                              </span>
                            </td>
                            <td>
                              <span className="sem-circle">{student.semester}</span>
                            </td>
                            <td className="td-mono">{student.mobileNo}</td>
                            <td>
                              <div className="action-btns">
                                <button className="print" onClick={() => handlePreview(student)}>
                                  👁️ <span className="btn-label">Preview</span>
                                </button>
                                <button className="edit-btn" onClick={() => handleEdit(student)}>
                                  ✏️ <span className="btn-label">Edit</span>
                                </button>
                                <button className="print" onClick={() => handlePrintPreview(student._id, student.photo)}>
                                  🖨️ <span className="btn-label">Print</span>
                                </button>
                                <button className="delete-button" onClick={() => handleDelete(student._id)}>
                                  🗑️ <span className="btn-label">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="empty-row">
                            No students found matching your criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {/* Alumni Header */}
                <div className="page-header alumni-page-header">
                  <div>
                    <h1>Alumni Network</h1>
                    <p>Connect with our graduated students</p>
                  </div>
                  <span className="header-badge">{alumni.length} alumni</span>
                </div>

                {/* Alumni Stats */}
                <div className="alumni-stats">
                  <div className="stat-card">
                    <h4>Total Alumni</h4>
                    <p>{alumni.length}</p>
                  </div>
                  <div className="stat-card">
                    <h4>Filtered</h4>
                    <p>{filteredAlumni.length}</p>
                  </div>
                  <div className="stat-card">
                    <h4>Graduation Years</h4>
                    <p>{alumniYears.length}</p>
                  </div>
                </div>

                {/* Alumni Toolbar */}
                <div className="toolbar">
                  <div className="toolbar-group">
                    <label className="filter-label">Course</label>
                    <select
                      className="filter-dropdown"
                      value={selectCourse}
                      onChange={(e) => setSelectCourse(e.target.value)}
                    >
                      <option value="">All courses</option>
                      <option value="B.Tech CSE">BTech CSE</option>
                      <option value="B.Tech ECE">BTech ECE</option>
                      <option value="BBA">BBA</option>
                      <option value="MCA">MCA</option>
                      <option value="BCA">BCA</option>
                    </select>
                  </div>

                  <div className="toolbar-divider" />

                  <div className="toolbar-group">
                    <label className="filter-label">Year</label>
                    <select
                      className="filter-dropdown"
                      value={selectedAlumniYear}
                      onChange={(e) => setSelectedAlumniYear(e.target.value)}
                    >
                      <option value="">All years</option>
                      {alumniYears.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div className="toolbar-spacer" />

                  <button className="alumni-toggle-btn" onClick={() => setShowAlumni(false)}>
                    📋 <span className="btn-label">View Students</span>
                  </button>
                </div>

                {/* Alumni Table */}
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Student</th>
                        <th>Admission No</th>
                        <th>Course</th>
                        <th>Academic Year</th>
                        <th>Mobile</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAlumni.length > 0 ? (
                        filteredAlumni.map((student, index) => (
                          <tr key={student._id}>
                            <td className="td-index">{index + 1}</td>
                            <td><strong>{student.name}</strong></td>
                            <td className="td-mono">{student.admissionNumber}</td>
                            <td>
                              <span className={courseBadgeClass(student.course)}>
                                {student.course}
                              </span>
                            </td>
                            <td>{student.academicYear}</td>
                            <td className="td-mono">{student.mobileNo}</td>
                            <td className="td-mono">{student.email || '—'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="empty-row">
                            No alumni records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : (
          <StudentEditForm
            student={selectedStudent}
            studentId={studentId}
            onSave={handleSaveComplete}
            onCancel={() => {
              setEditMode(false);
              setSelectedStudent(null);
            }}
            isOpen={editMode}
            showAdmissionNumber={true}
          />
        )}

        {showPreview && (
          <StudentPreviewModal
            student={previewStudent}
            onClose={() => {
              setShowPreview(false);
              setPreviewStudent(null);
            }}
            onEdit={handlePreviewEdit}
          />
        )}
        
{showMigrationPreview && migrationPreview && (
  <div className="migration-preview-modal">
    <div className="migration-preview-content">
      <div className="migration-preview-header">
        <h2>Alumni Migration Preview</h2>
        <button className="close-btn" onClick={() => setShowMigrationPreview(false)}>✕</button>
      </div>
      
      <div className="migration-summary">
        <div className="summary-card">
          <h4>Current Year</h4>
          <p>{migrationPreview.summary.currentYear}</p>
        </div>
        <div className="summary-card warning">
          <h4>Students to Migrate</h4>
          <p className="highlight">{migrationPreview.summary.toMigrate}</p>
        </div>
        <div className="summary-card">
          <h4>Students to Keep</h4>
          <p>{migrationPreview.summary.toKeep}</p>
        </div>
        <div className="summary-card">
          <h4>Total Students</h4>
          <p>{migrationPreview.summary.totalStudents}</p>
        </div>
      </div>
      
      {migrationPreview.studentsToMigrate.length > 0 ? (
        <>
          <h3>Students to be migrated ({migrationPreview.studentsToMigrate.length})</h3>
          <div className="table-responsive">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Admission No</th>
                  <th>Course</th>
                  <th>Academic Year</th>
                  <th>Graduated</th>
                </tr>
              </thead>
              <tbody>
                {migrationPreview.studentsToMigrate.map((student, index) => (
                  <tr key={student.id}>
                    <td>{index + 1}</td>
                    <td><strong>{student.name}</strong></td>
                    <td>{student.admissionNumber}</td>
                    <td>{student.course}</td>
                    <td>{student.academicYear}</td>
                    <td>{student.yearsAgo} year{student.yearsAgo > 1 ? 's' : ''} ago</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="migration-preview-footer">
            <button className="cancel-btn" onClick={() => setShowMigrationPreview(false)}>
              Cancel
            </button>
            <button className="confirm-migrate-btn" onClick={handleConfirmMigration} disabled={migrating}>
              {migrating ? 'Migrating...' : `Confirm Migration (${migrationPreview.summary.toMigrate} students)`}
            </button>
          </div>
        </>
      ) : (
        <div className="no-migration">
          <p>✅ No students need to be migrated at this time.</p>
          <button className="close-modal-btn" onClick={() => setShowMigrationPreview(false)}>
            Close
          </button>
        </div>
      )}
    </div>
  </div>
)}
      </div>
    </div>
  );
};

export default StudentListOfficer;